import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { Activity } from "lucide-react"
import { memo } from "react"

interface TokenUsageBadgeProps {
  inputTokens: number
  outputTokens: number
  workerType: string
  model?: string
}

const MODEL_PRICING: Record<string, { input: number; output: number }> = {
  'openai/gpt-4o': { input: 2.50, output: 10.00 },
  'openai/gpt-4o-mini': { input: 0.15, output: 0.60 },
  'openai/gpt-4-turbo': { input: 10.00, output: 30.00 },
  'openai/gpt-4': { input: 30.00, output: 60.00 },
  'openai/gpt-3.5-turbo': { input: 0.50, output: 1.50 },
  'openai/o1': { input: 15.00, output: 60.00 },
  'openai/o1-mini': { input: 3.00, output: 12.00 },
  'openai/o1-preview': { input: 15.00, output: 60.00 },
  'anthropic/claude-3-5-sonnet-latest': { input: 3.00, output: 15.00 },
  'anthropic/claude-3-5-haiku-latest': { input: 0.80, output: 4.00 },
  'anthropic/claude-3-opus-latest': { input: 15.00, output: 75.00 },
  'google/gemini-2.0-flash': { input: 0.10, output: 0.40 },
  'google/gemini-1.5-pro': { input: 1.25, output: 5.00 },
  'google/gemini-1.5-flash': { input: 0.075, output: 0.30 },
}

function calculateCost(inputTokens: number, outputTokens: number, model?: string): number | null {
  if (!model || !MODEL_PRICING[model]) return null
  
  const pricing = MODEL_PRICING[model]
  const inputCost = (inputTokens / 1000000) * pricing.input
  const outputCost = (outputTokens / 1000000) * pricing.output
  
  return inputCost + outputCost
}

export const TokenUsageBadge = memo(({ inputTokens, outputTokens, workerType, model }: TokenUsageBadgeProps) => {
  const totalTokens = inputTokens + outputTokens

  if (totalTokens === 0) {
    return null
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`
    }
    return num.toLocaleString()
  }

  const estimatedCost = calculateCost(inputTokens, outputTokens, model)

  return (
    <HoverCard openDelay={200}>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-1 px-2 py-0.5 bg-white/80 rounded-full text-xs font-medium text-gray-700 border border-gray-300 shadow-xs hover:shadow-md transition-shadow">
          <Activity size={12} className="text-blue-600" />
          <span>{formatNumber(totalTokens)}</span>
        </div>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" className="w-64 p-3" sideOffset={8}>
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={16} className="text-blue-600" />
            <h4 className="font-semibold text-sm">Token Usage</h4>
          </div>
          
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Input Tokens:</span>
              <span className="font-mono font-medium text-gray-900">
                {inputTokens.toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Output Tokens:</span>
              <span className="font-mono font-medium text-gray-900">
                {outputTokens.toLocaleString()}
              </span>
            </div>
            
            <div className="border-t pt-1.5 mt-1.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Total:</span>
                <span className="font-mono font-bold text-blue-600">
                  {totalTokens.toLocaleString()}
                </span>
              </div>
            </div>

            {estimatedCost !== null && (
              <div className="border-t pt-1.5 mt-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-medium">Est. Cost:</span>
                  <span className="font-mono font-bold text-green-600">
                    ${estimatedCost < 0.01 ? estimatedCost.toFixed(6) : estimatedCost.toFixed(4)}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
            <div>Worker: {workerType}</div>
            {model && <div className="mt-1">Model: {model.replace('openai/', '').replace('anthropic/', '').replace('google/', '')}</div>}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  )
})

TokenUsageBadge.displayName = "TokenUsageBadge"

